import React from 'react';
import { Box, IconButton, Stack, Tooltip, Typography, keyframes } from '@mui/material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

/**
 * Page header for the Sales Operations screens.
 *
 * Title and description on the left, provenance and controls on the right,
 * over a rule — the same shape the rest of the app uses (see
 * `settings/components/SettingsPageHeader`), which has no title slot, hence
 * this one.
 *
 * The right-hand side exists to answer "how old is this?" before anyone has
 * to ask. These screens are served from a 24-hour cache, so a page that
 * showed only numbers would be quietly implying they were fetched the moment
 * you looked. `asOf` is the data's own anchor date (the latest call in the
 * warehouse); `computedAt` is when this payload was built.
 */
export default function SalesOpsHeader({
  title,
  description,
  asOf,
  computedAt,
  fromCache,
  onRefresh,
  refreshing,
}: {
  title: string;
  description?: string;
  /** The data's anchor date — the latest call the warehouse holds. */
  asOf?: string | null;
  /** ISO timestamp this payload was computed. */
  computedAt?: string | null;
  fromCache?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
}) {
  const computedLabel = React.useMemo(() => {
    if (!computedAt) return null;
    const d = new Date(computedAt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }, [computedAt]);

  return (
    <Box
      sx={(t) => ({
        mb: 2.5,
        pb: 1.75,
        borderBottom: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        flexWrap: 'wrap',
      })}
    >
      <Box>
        <Typography sx={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25, lineHeight: 1.45 }}
          >
            {description}
          </Typography>
        )}
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ pt: 0.25 }}>
        {asOf && (
          <Stack alignItems="flex-end" spacing={0}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              As of {asOf}
            </Typography>
            {computedLabel && (
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                {fromCache ? `cached ${computedLabel}` : `computed ${computedLabel}`}
              </Typography>
            )}
          </Stack>
        )}
        {onRefresh && (
          <Tooltip
            arrow
            title={
              refreshing
                ? 'Recomputing from the warehouse…'
                : 'Recompute from the warehouse now, bypassing the 24-hour cache'
            }
          >
            {/* span so the tooltip still shows while the button is disabled */}
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={refreshing}
                sx={{ '& svg': refreshing ? { animation: `${spin} 1s linear infinite` } : {} }}
              >
                <RefreshRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
